import { configureStore } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import auth from './slices/authSlice';
import ui from './slices/uiSlice';
import { toPermissionSet, can, canAll, canAny } from '@/lib/auth/permissions';

export const store = configureStore({
  reducer: { auth, ui },
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

/** Typed hooks — always use these instead of the bare react-redux ones. */
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();

// ── permission selectors ────────────────────────────────────────────────────
// Exposed as selectors so components never re-implement the wildcard/manage
// rules; there is exactly one place that knows them (lib/auth/permissions).

export const selectPermissions = (s: RootState) => s.auth.permissions;
export const selectUser = (s: RootState) => s.auth.user;
export const selectIsAuthenticated = (s: RootState) => s.auth.isAuthenticated;

export const selectCan = (required: string) => (s: RootState) =>
  can(toPermissionSet(s.auth.permissions), required);

export const selectCanAll = (required: readonly string[]) => (s: RootState) =>
  canAll(toPermissionSet(s.auth.permissions), required);

export const selectCanAny = (required: readonly string[]) => (s: RootState) =>
  canAny(toPermissionSet(s.auth.permissions), required);
